import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './UpdatesTabContent.module.css';
import { fetchUnitUpdateDetails, deleteUnitUpdate, updateUnitUpdate } from '../services/unitService';
import { showSuccessNotification } from '../store/slices/notificationSlice';

export default function UnitUpdateDetails({ updateId, onBack, onDelete, onEdit, loading, error }) {
  const dispatch = useDispatch();

  // Get unit update permissions from cached permissions
  const {
    hasDeleteUnitUpdates,
    hasUpdateUnitUpdates
  } = useCachedPermissions();

  const [details, setDetails] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editImages, setEditImages] = useState([]); // {id, src, file, url}
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setFetchLoading(true);
    setFetchError(null);
    fetchUnitUpdateDetails(updateId)
      .then(res => {
        if (isMounted) {
          setDetails(res.data);
          setEditDescription(res.data.description || '');
          setEditImages((res.data.images || []).map((url, idx) => ({ id: `url-${idx}`, src: url, url })));
          setFetchLoading(false);
        }
      })
      .catch(err => {
        if (isMounted) {
          setFetchError(err.message);
          setFetchLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [updateId]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    if (onDelete) {
      await onDelete(updateId);
    }
  };

  const handleImageUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.onchange = (e) => {
      const files = Array.from(e.target.files);
      files.forEach((file, idx) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const newId = Date.now() + idx;
          setEditImages(prev => [...prev, { id: newId, src: event.target.result, file }]);
        };
        reader.readAsDataURL(file);
      });
    };
    fileInput.click();
  };

  const removeImage = (id) => {
    setEditImages(prev => prev.filter(img => img.id !== id));
  };

  const handleEdit = () => {
    setEditMode(true);
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditDescription(details.description || '');
    setEditImages((details.images || []).map((url, idx) => ({ id: `url-${idx}`, src: url, url })));
    setEditError('');
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    setEditError('');
    try {
      // Separate old images (that should remain) from new images
      const oldImages = editImages.filter(img => img.url && !img.file).map(img => img.url);
      const newImages = editImages.filter(img => img.file).map(img => img.file);

      const res = await updateUnitUpdate(updateId, {
        description: editDescription,
        images: newImages,
        old_img: oldImages
      });

      // After update, refetch details
      const detailsRes = await fetchUnitUpdateDetails(updateId);
      setDetails(detailsRes.data);
      setEditMode(false);

      // Show success notification
      dispatch(showSuccessNotification('Unit update saved successfully!'));

      if (onEdit) onEdit(detailsRes.data);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  if (fetchLoading) return <div className={styles.noDataMessage}><p>Loading...</p></div>;
  if (fetchError) return <div className={styles.noDataMessage}><p>{fetchError}</p></div>;
  if (!details) return null;

  return (
    <div className={styles.updatesContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={onBack} disabled={editLoading || loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className={styles.headerTitle}>Update Details</h2>
        </div>
        <div className={styles.headerActions}>
          {hasDeleteUnitUpdates && (
            <button className={styles.deleteButton} onClick={handleDelete} disabled={editLoading || loading}>Delete</button>
          )}
          {editMode ? (
            hasUpdateUnitUpdates && (
              <>
                <button className={styles.saveButton} onClick={handleSaveEdit} disabled={editLoading || loading}>{editLoading ? 'Saving...' : 'Save'}</button>
                <button className={styles.cancelButton} onClick={handleCancelEdit} disabled={editLoading || loading}>Cancel</button>
              </>
            )
          ) : (
            hasUpdateUnitUpdates && (
              <button className={styles.saveButton} onClick={handleEdit} disabled={editLoading || loading}>Edit</button>
            )
          )}
        </div>
      </div>
      {error && <div className={styles.formError}>{error}</div>}
      <div className={styles.unitUpdateDetailsCard}>
        <div className={styles.unitUpdateImages}>
          {editMode ? (
            <>
              <div className={styles.imageGrid}>
                {editImages.map(img => (
                  <div key={img.id} className={styles.imageItem}>
                    <img src={img.src || img.url} alt="Preview" />
                    <button type="button" className={styles.removeButton} onClick={() => removeImage(img.id)} disabled={editLoading}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.uploadButton} onClick={handleImageUpload} disabled={editLoading}>
                Upload More Images
              </button>
            </>
          ) : (
            details.images && details.images.length > 0 ? (
              details.images.map((img, idx) => (
                <img key={idx} src={img} alt={`Update Image ${idx + 1}`} className={styles.unitUpdateImage} />
              ))
            ) : (
              <div style={{ width: '100%', height: '200px', background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', borderRadius: 8 }}>No Image</div>
            )
          )}
        </div>
        <div className={styles.unitUpdateInfo}>
          <span className={styles.unitUpdateLabel}>Unit Name</span>
          <h3 className={styles.unitCardTitle}>{details.unit_name}</h3>
          <span className={styles.unitUpdateLabel}>Description</span>
          {editMode ? (
            <textarea
              className={styles.editTextarea}
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              rows={6}
              disabled={editLoading}
              placeholder="Enter update description..."
            />
          ) : (
            <p className={styles.unitCardDesc}>{details.description}</p>
          )}
          <div className={styles.unitUpdateMeta}>
            <div>
              <span className={styles.unitUpdateLabel}>Date</span>
              <span className={styles.unitUpdateDate}>{details.created_date}</span>
            </div>
            <div>
              <span className={styles.unitUpdateLabel}>Time</span>
              <span className={styles.unitUpdateTime}>{details.created_time}</span>
            </div>
          </div>
          {editError && <div className={styles.formError}>{editError}</div>}
        </div>
      </div>
    </div>
  );
} 